// Workflow Builder v2 — supabase.functions.invoke wrapped in an AbortController
// + ClassifiedError mapping.
//
// The Supabase client doesn't expose a timeout option on functions.invoke. This
// helper races the invoke against a manual timer; when the timer fires, we
// surface the same ClassifiedError shape PromptBuilder uses, so the UI can
// reuse the existing error-state pattern.

import { supabase } from '@/integrations/supabase/client';
import { classifyError, type ClassifiedError } from '@/lib/fetchWithTimeout';

interface InvokeWithTimeoutResult<T> {
  data: T | null;
  error: ClassifiedError | null;
}

/** Class so we can recognize the timeout in classifyError. */
class InvokeTimeoutError extends Error {
  name = 'AbortError';
}

export async function invokeWithTimeout<T = unknown>(
  functionName: string,
  body: Record<string, unknown> | undefined,
  timeoutMs: number,
): Promise<InvokeWithTimeoutResult<T>> {
  let timer: ReturnType<typeof setTimeout> | null = null;

  const invokePromise = supabase.functions.invoke<T>(functionName, body ? { body } : undefined);

  const timeoutPromise = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new InvokeTimeoutError(`Invoke ${functionName} timed out after ${timeoutMs}ms`)), timeoutMs);
  });

  try {
    const result = await Promise.race([invokePromise, timeoutPromise]);
    // Type assertion: race resolves with invoke's shape when it wins.
    const typed = result as { data: T | null; error: { message?: string; status?: number; context?: unknown } | null };
    if (typed.error) {
      // Try to dig the HTTP status out of the error context (FunctionsHttpError carries it).
      const ctx = typed.error.context as { status?: number; res?: Response } | undefined;
      const status = ctx?.status ?? ctx?.res?.status;
      return { data: null, error: classifyError(typed.error, status, `functions/${functionName}`) };
    }
    return { data: typed.data, error: null };
  } catch (err) {
    return { data: null, error: classifyError(err, undefined, `functions/${functionName}`) };
  } finally {
    if (timer !== null) clearTimeout(timer);
  }
}
